import { useEffect, useState } from 'react';
import { accreditationApi, auditAlertsApi, auditApi, type AccreditationFile } from '../lib/api';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export function InstitutionRegimePage() {
  const [tab, setTab] = useState<'regime' | 'audit'>('regime');
  const [pending, setPending] = useState<AccreditationFile[]>([]);
  const [declare, setDeclare] = useState({
    institution_id: '', regime: 'FREE', country: '', authority_name: '', legal_reference: '',
  });
  const [fileId, setFileId] = useState('');
  const [doc, setDoc] = useState({ doc_type: 'CHARTER', title: '', media_ref: '', reference_number: '' });
  const [audit, setAudit] = useState({
    course_version_id: '', subscriber_kind: 'INSTITUTION', organisation_name: '',
    contact_name: '', contact_email: '', contact_phone: '', country: '', alert_lead_minutes: 30,
  });
  const [slots, setSlots] = useState([{ weekday: 1, starts_at: '09:00', ends_at: '11:00', timezone_name: 'UTC' }]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [dueAlerts, setDueAlerts] = useState<any[]>([]);
  const [subscriptionId, setSubscriptionId] = useState('');

  useEffect(() => { loadPending(); auditAlertsApi.due().then(setDueAlerts).catch(() => setDueAlerts([])); }, []);

  async function loadPending() {
    try { setPending(await accreditationApi.pending()); }
    catch { /* réservé aux administrateurs */ }
  }

  async function run(fn: () => Promise<any>, note: string) {
    setError(''); setMessage('');
    try { const r = await fn(); setMessage(note); await loadPending(); return r; }
    catch (e: any) { setError(String(e.message || e)); }
  }

  return <section className="page">
    <header className="page-header">
      <div>
        <span className="eyebrow">ÉTABLISSEMENTS</span>
        <h1>Régime et cours d'audit</h1>
        <p>Un établissement libre ne fournit aucune pièce. Un établissement reconnu soumet ses documents à vérification.</p>
      </div>
    </header>

    <div className="tab-row">
      <button className={`button ${tab === 'regime' ? 'primary' : 'secondary'}`} onClick={() => setTab('regime')}>Régime</button>
      <button className={`button ${tab === 'audit' ? 'primary' : 'secondary'}`} onClick={() => setTab('audit')}>Cours d'audit</button>
    </div>

    {error && <div className="alert error">{error}</div>}
    {message && <div className="notice">{message}</div>}

    {tab === 'regime' && <>
      <div className="card">
        <h3>Déclarer un régime</h3>
        <label>Établissement
          <input value={declare.institution_id} onChange={(e: any) => setDeclare({ ...declare, institution_id: e.target.value })} placeholder="institution_id" />
        </label>
        <label>Régime demandé
          <select value={declare.regime} onChange={(e: any) => setDeclare({ ...declare, regime: e.target.value })}>
            <option value="FREE">Libre — aucune pièce à fournir</option>
            <option value="ACCREDITED">Reconnu — pièces justificatives exigées</option>
          </select>
        </label>
        {declare.regime === 'ACCREDITED' && <>
          <label>Pays<input value={declare.country} onChange={(e: any) => setDeclare({ ...declare, country: e.target.value })} /></label>
          <label>Autorité de tutelle<input value={declare.authority_name} onChange={(e: any) => setDeclare({ ...declare, authority_name: e.target.value })} /></label>
          <label>Référence légale<input value={declare.legal_reference} onChange={(e: any) => setDeclare({ ...declare, legal_reference: e.target.value })} /></label>
        </>}
        <button className="button primary" disabled={!declare.institution_id.trim()}
          onClick={() => run(async () => { const f = await accreditationApi.declare(declare); setFileId(f.id || ''); return f; },
            declare.regime === 'FREE' ? 'Régime libre enregistré, sans pièce requise.' : 'Dossier ouvert. Ajoutez les pièces puis lancez la vérification.')}>
          Déclarer
        </button>
      </div>

      {declare.regime === 'ACCREDITED' && <div className="card">
        <h3>Pièces justificatives</h3>
        <label>Dossier<input value={fileId} onChange={(e: any) => setFileId(e.target.value)} placeholder="file_id" /></label>
        <label>Type<input value={doc.doc_type} onChange={(e: any) => setDoc({ ...doc, doc_type: e.target.value })} /></label>
        <label>Intitulé<input value={doc.title} onChange={(e: any) => setDoc({ ...doc, title: e.target.value })} /></label>
        <label>Référence du document<input value={doc.reference_number} onChange={(e: any) => setDoc({ ...doc, reference_number: e.target.value })} /></label>
        <label>Fichier<input value={doc.media_ref} onChange={(e: any) => setDoc({ ...doc, media_ref: e.target.value })} placeholder="média" /></label>
        <div className="toolbar">
          <button className="button secondary" disabled={!fileId.trim()} onClick={() => run(() => accreditationApi.addDocument(fileId, doc), 'Pièce ajoutée.')}>Ajouter la pièce</button>
          <button className="button primary" disabled={!fileId.trim()} onClick={() => run(() => accreditationApi.aiCheck(fileId), 'Vérification automatique effectuée.')}>Lancer la vérification IA</button>
        </div>
        <p className="technical-note">En cas de doute, le dossier est transmis à un vérificateur humain.</p>
      </div>}

      <div className="card">
        <h3>Dossiers en attente de vérification humaine</h3>
        {pending.map(f => <div key={f.id} className="order-row">
          <strong>{f.institution_id.slice(0, 8)} — {f.requested_regime}</strong>
          <span>{f.status}{f.ai_notes ? ` · ${f.ai_notes}` : ''}</span>
          <div className="toolbar">
            <button className="button primary" onClick={() => run(() => accreditationApi.review(f.id, true, ''), 'Établissement reconnu.')}>Valider</button>
            <button className="button secondary" onClick={() => run(() => accreditationApi.review(f.id, false, 'pièces non probantes'), 'Rejeté : régime libre attribué.')}>Rejeter</button>
          </div>
        </div>)}
        {!pending.length && <p className="technical-note">Aucun dossier en attente.</p>}
      </div>
    </>}

    {tab === 'audit' && <div className="card">
      <h3>Utiliser un cours avec l'IA comme professeur</h3>
      <p className="technical-note">Sans créer d'établissement. Un horaire est obligatoire : il déclenche les alertes.</p>
      <label>Cours<input value={audit.course_version_id} onChange={(e: any) => setAudit({ ...audit, course_version_id: e.target.value })} placeholder="course_version_id" /></label>
      <label>Souscripteur
        <select value={audit.subscriber_kind} onChange={(e: any) => setAudit({ ...audit, subscriber_kind: e.target.value })}>
          <option value="INSTITUTION">Établissement</option>
          <option value="INDIVIDUAL">Particulier</option>
        </select>
      </label>
      {audit.subscriber_kind === 'INSTITUTION' &&
        <label>Nom de l'établissement<input value={audit.organisation_name} onChange={(e: any) => setAudit({ ...audit, organisation_name: e.target.value })} /></label>}
      <label>Contact<input value={audit.contact_name} onChange={(e: any) => setAudit({ ...audit, contact_name: e.target.value })} /></label>
      <label>Courriel<input value={audit.contact_email} onChange={(e: any) => setAudit({ ...audit, contact_email: e.target.value })} /></label>
      <label>Téléphone<input value={audit.contact_phone} onChange={(e: any) => setAudit({ ...audit, contact_phone: e.target.value })} /></label>
      <label>Pays<input value={audit.country} onChange={(e: any) => setAudit({ ...audit, country: e.target.value })} /></label>
      <label>Alerte (minutes avant)
        <input type="number" value={audit.alert_lead_minutes} onChange={(e: any) => setAudit({ ...audit, alert_lead_minutes: Number(e.target.value) })} />
      </label>

      <h4>Horaire hebdomadaire</h4>
      {slots.map((s, i) => <div key={i} className="slot-row">
        <select value={s.weekday} onChange={(e: any) => { const n = [...slots]; n[i] = { ...s, weekday: Number(e.target.value) }; setSlots(n); }}>
          {JOURS.map((j, k) => <option key={k} value={k}>{j}</option>)}
        </select>
        <input type="time" value={s.starts_at} onChange={(e: any) => { const n = [...slots]; n[i] = { ...s, starts_at: e.target.value }; setSlots(n); }} />
        <input type="time" value={s.ends_at} onChange={(e: any) => { const n = [...slots]; n[i] = { ...s, ends_at: e.target.value }; setSlots(n); }} />
        {slots.length > 1 && <button className="button secondary" onClick={() => setSlots(slots.filter((_, k) => k !== i))}>Retirer</button>}
      </div>)}
      <button className="button secondary" onClick={() => setSlots([...slots, { weekday: 1, starts_at: '09:00', ends_at: '11:00', timezone_name: 'UTC' }])}>Ajouter un créneau</button>

      <button className="button primary" disabled={!audit.course_version_id.trim()}
        onClick={() => run(() => auditApi.subscribe({ ...audit, slots }), "Souscription enregistrée. Les alertes sont planifiées.")}>
        Souscrire
      </button>

      <h4>Alertes planifiées</h4>
      <label>Souscription<input value={subscriptionId} onChange={(e: any) => setSubscriptionId(e.target.value)} placeholder="subscription_id" /></label>
      <button className="button secondary" disabled={!subscriptionId.trim()}
        onClick={() => run(async () => { setDueAlerts(await auditAlertsApi.alerts(subscriptionId.trim())); }, 'Alertes replanifiées.')}>
        Replanifier les alertes
      </button>
      {dueAlerts.map(a => <div key={a.id} className="order-row">
        <strong>{(a.subscription_id || '').slice(0, 8)}</strong>
        <span>{a.scheduled_for || a.due_at || ''} · {a.status}</span>
        <button className="button secondary" onClick={() => run(() => auditAlertsApi.acknowledge(a.id), 'Alerte acquittée.')}>Acquitter</button>
      </div>)}
      {!dueAlerts.length && <p className="technical-note">Aucune alerte en attente.</p>}
    </div>}
  </section>;
}
